import React from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';

function normalizeMessage(value) {
  const msg = typeof value === 'string' ? value.trim() : '';
  return msg || '';
}

export function FloatingNotices({ error, success, info, warning, onDismissError, onDismissSuccess, onDismissInfo, onDismissWarning }) {
  const errorMsg = normalizeMessage(error);
  const successMsg = normalizeMessage(success);
  const infoMsg = normalizeMessage(info);
  const warningMsg = normalizeMessage(warning);
  const hasAny = Boolean(errorMsg || successMsg || infoMsg || warningMsg);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {hasAny && (
        <motion.div
          className="floating-notice-stack"
          initial={{ opacity: 0, y: -14, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.98 }}
          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
        >
          {errorMsg ? (
            <motion.div
              className="floating-notice error"
              role="alert"
              initial={{ opacity: 0, y: -8, filter: 'blur(2px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              exit={{ opacity: 0, y: -8, filter: 'blur(1px)' }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="floating-notice-icon" aria-hidden="true">
                <AlertCircle size={18} />
              </span>
              <span className="floating-notice-message">{errorMsg}</span>
              <button
                type="button"
                className="floating-notice-close"
                onClick={() => (typeof onDismissError === 'function' ? onDismissError() : undefined)}
                aria-label="Dismiss error"
              >
                <X size={16} />
              </button>
            </motion.div>
          ) : null}
          {warningMsg ? (
            <motion.div
              className="floating-notice warning"
              role="status"
              initial={{ opacity: 0, y: -8, filter: 'blur(2px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              exit={{ opacity: 0, y: -8, filter: 'blur(1px)' }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="floating-notice-icon" aria-hidden="true">
                <AlertTriangle size={18} />
              </span>
              <span className="floating-notice-message">{warningMsg}</span>
              <button
                type="button"
                className="floating-notice-close"
                onClick={() => (typeof onDismissWarning === 'function' ? onDismissWarning() : undefined)}
                aria-label="Dismiss warning"
              >
                <X size={16} />
              </button>
            </motion.div>
          ) : null}
          {infoMsg ? (
            <motion.div
              className="floating-notice info"
              role="status"
              initial={{ opacity: 0, y: -8, filter: 'blur(2px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              exit={{ opacity: 0, y: -8, filter: 'blur(1px)' }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="floating-notice-icon" aria-hidden="true">
                <Info size={18} />
              </span>
              <span className="floating-notice-message">{infoMsg}</span>
              <button
                type="button"
                className="floating-notice-close"
                onClick={() => (typeof onDismissInfo === 'function' ? onDismissInfo() : undefined)}
                aria-label="Dismiss message"
              >
                <X size={16} />
              </button>
            </motion.div>
          ) : null}
          {successMsg ? (
            <motion.div
              className="floating-notice success"
              role="status"
              initial={{ opacity: 0, y: -8, filter: 'blur(2px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              exit={{ opacity: 0, y: -8, filter: 'blur(1px)' }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="floating-notice-icon" aria-hidden="true">
                <CheckCircle2 size={18} />
              </span>
              <span className="floating-notice-message">{successMsg}</span>
              <button
                type="button"
                className="floating-notice-close"
                onClick={() => (typeof onDismissSuccess === 'function' ? onDismissSuccess() : undefined)}
                aria-label="Dismiss success"
              >
                <X size={16} />
              </button>
            </motion.div>
          ) : null}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

